import { type ClassValue, clsx } from 'clsx';
import { AlertCircle, CircleCheck, Clock } from 'lucide-react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { BadgeProps } from '../components/badge';
import type { Currency } from '../types';
import { PAYROLL_STATUS } from './constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
/**
 * Valid types for query parameter values
 */
type QueryParamValue =
  | string
  | number
  | boolean
  | (string | number | boolean)[]
  | { from?: string | number; to?: string | number }
  | { by: string; order: string };

/**
 * Builds URL query parameters from an object, handling arrays and filtering out empty values
 * @param params - Object containing query parameters
 * @returns URLSearchParams object with properly formatted query parameters
 */
export function buildQueryParams(
  params: Record<string, QueryParamValue | null | undefined>
): URLSearchParams {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    else if (typeof value === 'object' && value !== null && 'by' in value && 'order' in value) {
      // Handle sort objects: sort[by]=name&sort[order]=asc
      queryParams.append(`${key}[by]`, (value as { by: string; order: string }).by);
      queryParams.append(`${key}[order]`, (value as { by: string; order: string }).order);
    } else if (typeof value === 'object' && value !== null && ('from' in value || 'to' in value)) {
      // Handle objects with from/to properties: key[from]=value&key[to]=value
      const hasValidFrom = value.from !== undefined && value.from !== null && value.from !== '';
      const hasValidTo = value.to !== undefined && value.to !== null && value.to !== '';

      // Only add parameters if at least one of from or to has a valid value
      if (hasValidFrom || hasValidTo) {
        if (hasValidFrom && value.from !== undefined) {
          queryParams.append(`${key}[from]`, value.from.toString());
        }
        if (hasValidTo && value.to !== undefined) {
          queryParams.append(`${key}[to]`, value.to.toString());
        }
      }
    } else if (typeof value === 'string' && value.trim() === '') return;
    else if (Array.isArray(value) && value.length === 0) return;
    else if (Array.isArray(value)) {
      // For arrays, use bracket notation: key[]=value
      value.forEach((v) => queryParams.append(`${key}[]`, v.toString()));
    } else {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams;
}

export function detectDeviceType(userAgent: string): 'mobile' | 'tablet' | 'desktop' {
  // Convert to lowercase for easier matching
  const ua = userAgent.toLowerCase();

  // Mobile detection patterns
  const mobilePatterns = [
    /android.*mobile/,
    /iphone/,
    /ipod/,
    /blackberry/,
    /windows phone/,
    /opera mini/,
    /mobile.*firefox/,
    /mobile.*chrome/,
    /mobile.*safari/,
  ];

  // Tablet detection patterns
  const tabletPatterns = [/ipad/, /android(?!.*mobile)/, /tablet/, /playbook/, /kindle/];

  // Check for mobile first
  if (mobilePatterns.some((pattern) => pattern.test(ua))) {
    return 'mobile';
  }

  // Check for tablet
  if (tabletPatterns.some((pattern) => pattern.test(ua))) {
    return 'tablet';
  }

  // Default to desktop
  return 'desktop';
}

/**
 * Checks if the user is a master admin
 * @param roles - The user's roles
 * @returns True if the user is a master admin, false otherwise
 */
export const isMasterAdmin = (roles: string[]) => {
  return roles.includes('admin');
};

export function snakeToCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

export function camelToSnakeCase(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export const normalizeCaseMap = {
  snakeToCamelCase,
  camelToSnakeCase,
};

export type NormalizeCaseRegistry = keyof typeof normalizeCaseMap;

/**
 * Gets the currency code from currency_id
 * @param currencyId - The currency ID
 * @param currencies - Available currencies
 * @returns The currency code
 */
export const getCurrencyCode = (currencyId: number, currencies: Currency[]): string => {
  const currency = currencies.find((c) => c.id === currencyId);

  return currency?.code || 'EUR';
};

/**
 * Determines the MIME type for a file based on API extension, document type, or filename.
 * @param documentType - Semantic type or format hint (e.g. 'contract', 'pdf')
 * @param fileName - The file name (may include extension)
 * @param extensionFromApi - Optional extension from API metadata (e.g. 'pdf'); checked first when present
 * @returns MIME type string (e.g. 'application/pdf')
 */
export const getMimeType = (
  documentType: string,
  fileName: string,
  extensionFromApi?: string | null
): string => {
  const typeMappings: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    csv: 'text/csv',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
  };

  const normalizedExtension = extensionFromApi?.trim().replace(/^\./, '').toLowerCase() ?? '';
  if (normalizedExtension && normalizedExtension in typeMappings) {
    return typeMappings[normalizedExtension]!;
  }

  const type = documentType?.toLowerCase();
  if (type && type in typeMappings) {
    return typeMappings[type]!;
  }

  const extensionFromFileName = fileName.split('.').pop()?.toLowerCase();
  if (extensionFromFileName && extensionFromFileName in typeMappings) {
    return typeMappings[extensionFromFileName]!;
  }

  // Default to octet-stream for unknown types
  return 'application/octet-stream';
};

export const getStatusStyle = (
  status: string
): { color: BadgeProps['variant']; icon: ReactNode; label: string } => {
  switch (status) {
    case PAYROLL_STATUS.APPROVED:
      return {
        color: 'green' as const,
        icon: <CircleCheck />,
        label: 'Approved',
      };
    case PAYROLL_STATUS.PENDING:
      return {
        color: 'dark-grey' as const,
        icon: <Clock />,
        label: 'Pending',
      };
    case PAYROLL_STATUS.DECLINED:
      return {
        color: 'red' as const,
        icon: <AlertCircle />,
        label: 'Rejected',
      };
    default:
      return {
        color: 'dark-grey' as const,
        icon: <Clock />,
        label: 'Pending',
      };
  }
};
